import { Box3, Vector3 } from 'three'
import type { TilesViewSnapshot, TileTextureQualityPolicy } from '@/plugins/tiles/contracts'
import type { RuntimeTileNode } from '@/plugins/tiles/hierarchy/ExplicitHierarchyProvider'

/** 稳定观察只设置一次到期唤醒；不需要等待期间持续 RAF。 */
export class TileTextureQualityController {
  private _viewKey = ''
  private _lastMotion = -Infinity
  private _lastChange = new Map<string, number>()
  private _uploadCap = Infinity

  constructor(readonly policy: TileTextureQualityPolicy) {}

  observe(views: readonly TilesViewSnapshot[], now: number, overrunBytes?: number): void {
    const key = views
      .map((view) =>
        [
          view.viewId,
          ...view.cameraPosition.toArray(),
          ...view.projectionMatrix.elements,
          ...view.placement.elements,
          ...view.frustum.planes.flatMap((plane) => [...plane.normal.toArray(), plane.constant]),
        ].join(',')
      )
      .join('|')
    if (key !== this._viewKey) {
      this._lastMotion = now
      this._viewKey = key
    }
    if (overrunBytes && overrunBytes >= 4 * 1024 ** 2)
      this._uploadCap = Math.min(this._uploadCap, overrunBytes > 24 * 1024 ** 2 ? 2048 : 1024)
  }

  choose(
    node: RuntimeTileNode,
    views: readonly TilesViewSnapshot[],
    now: number,
    pressure: number
  ): number {
    const cap = this.policy.maxTextureSize
    if (this.policy.mode === 'fixed') return cap
    if (now - this._lastMotion < this.policy.stableMs || pressure >= 0.9) return Math.min(cap, 1024)
    let pixels = 0
    const bounds = new Box3(),
      center = new Vector3(),
      size = new Vector3()
    for (const view of views) {
      bounds.copy(node.bounds).applyMatrix4(view.placement)
      if (!view.frustum.intersectsBox(bounds)) continue
      bounds.getCenter(center)
      bounds.getSize(size)
      const distance = Math.max(0.01, center.distanceTo(view.cameraPosition) - size.length() / 2)
      pixels = Math.max(
        pixels,
        (size.length() * view.cssHeight * Math.abs(view.projectionMatrix.elements[5])) /
          (view.perspective ? 2 * distance : 2)
      )
    }
    return Math.min(
      cap,
      this._uploadCap,
      pressure >= 0.8 ? 2048 : Infinity,
      pixels > 1000 ? 4096 : pixels > 350 ? 2048 : 1024
    )
  }

  canUpgrade(id: string, now: number): boolean {
    return now >= (this._lastChange.get(id) ?? -Infinity) + this.policy.cooldownMs
  }
  changed(id: string, now: number): void {
    if (this._lastChange.size >= 4096 && !this._lastChange.has(id))
      this._lastChange.delete(this._lastChange.keys().next().value!)
    this._lastChange.set(id, now)
  }
  nextCheck(now: number): number | undefined {
    const deadlines = [
      this._lastMotion + this.policy.stableMs,
      ...[...this._lastChange.values()].map((time) => time + this.policy.cooldownMs),
    ].filter((time) => time > now)
    return deadlines.length ? Math.min(...deadlines) - now : undefined
  }
}
